import {
  createAddressExportColumns,
  type IndexTableExportColumnSource,
  type IndexTableExportSheet,
  createIndexTableExportColumn,
} from '@/components/tables/indexTableExport'
import { formatPhone as defaultFormatPhone, type PhoneFormatter } from '@/hooks/use-phone-formatter'
import { getLineItemDisplay } from '@/lib/line-item-display'
import { CustomerPaymentTerms } from '@/lib/enums/CustomerPaymentTerms'
import { FulfillmentType } from '@/lib/enums/FulfillmentType'
import { PaymentRefundStatus } from '@/lib/enums/PaymentRefundStatus'
import { PaymentStatus } from '@/lib/enums/PaymentStatus'
import { PaymentType } from '@/lib/enums/PaymentType'
import { SalesOrderFulfillmentStatus } from '@/lib/enums/SalesOrderFulfillmentStatus'
import { SalesOrderPaymentStatus } from '@/lib/enums/SalesOrderPaymentStatus'
import { SalesOrderRefundStatus } from '@/lib/enums/SalesOrderRefundStatus'
import { TransactionLineType } from '@/lib/enums/TransactionLineType'
import { TransactionSource } from '@/lib/enums/TransactionSource'
import { TransactionType } from '@/lib/enums/TransactionType'
import { formatStripePaymentMethodType } from '@/lib/payment-method-display'
import type {
  SalesOrderCashActivity,
  SalesOrderDocumentLine,
  SalesOrderListItem,
} from '@/lib/services/sales-orders/sales-orders.types'
import {
  getEnumDisplayName,
  normalizeCurrencyValue,
  type EnumEntry,
  type NumericInput,
} from '@/lib/utils'
import {
  getSalesOrderCollectionClosedReason,
  getSalesOrderCollectionStatus,
  getSalesOrderGrossPaidTotal,
  getSalesOrderGrossRefundedTotal,
  getSalesOrderNetCollectedTotal,
  getSalesOrderRemainingTotal,
  isCancelledSalesOrder,
} from './sales-order-presentation'

type ExportColumn<TData> = IndexTableExportColumnSource<TData>

type SalesOrderLineItemExportRow = {
  order: SalesOrderListItem
  lineItem: SalesOrderDocumentLine
  lineNumber: number
}

type SalesOrderTransactionExportRow = {
  order: SalesOrderListItem
  transaction: SalesOrderCashActivity
  transactionNumber: number
}

function getEnumLabel(
  enumObj: Record<string, EnumEntry>,
  value: string | null | undefined
): string {
  return value ? getEnumDisplayName(enumObj, value) : ''
}

// A cancelled order reads Cancelled, refunded or not; the Refund Status column says what was refunded.
function getSalesOrderPaymentStatusLabel(order: SalesOrderListItem): string {
  return isCancelledSalesOrder(order)
    ? 'Cancelled'
    : getEnumLabel(SalesOrderPaymentStatus, order.payment_status)
}

function getMoney(value: NumericInput): number {
  return normalizeCurrencyValue(value ?? 0)
}

// Blank when the API cannot know the amount (unavailable financial documents), never zero.
function getKnownMoney(value: NumericInput): number | null {
  return value == null ? null : normalizeCurrencyValue(value)
}

// The payloads carry E.164 phones and no country code, so numbers format as US by default.
function getPhone(
  value: string | null | undefined,
  formatPhone: PhoneFormatter['formatPhone'] = defaultFormatPhone
): string {
  return value ? formatPhone(value) : ''
}

export function getSalesOrderSummaryExportColumns(
  showOrganization = false,
  formatPhone?: PhoneFormatter['formatPhone']
): ExportColumn<SalesOrderListItem>[] {
  return [
    createIndexTableExportColumn({
      id: 'order_id',
      label: 'Order ID',
      type: 'integer',
      value: (order: SalesOrderListItem) => order.id,
    }),
    createIndexTableExportColumn({
      id: 'reference_id',
      label: 'Reference ID',
      value: (order: SalesOrderListItem) => order.reference_id || '',
    }),
    showOrganization
      ? createIndexTableExportColumn({
          id: 'organization',
          label: 'Organization',
          value: (order: SalesOrderListItem) => order.organization_name || '',
        })
      : null,
    createIndexTableExportColumn({
      id: 'source',
      label: 'Source',
      value: (order: SalesOrderListItem) => order.source || '',
    }),
    createIndexTableExportColumn({
      id: 'purchaser',
      label: 'Purchaser',
      value: (order: SalesOrderListItem) => order.purchaser?.name || '',
    }),
    createIndexTableExportColumn({
      id: 'purchaser_email',
      label: 'Purchaser Email',
      value: (order: SalesOrderListItem) => order.purchaser?.email || '',
    }),
    createIndexTableExportColumn({
      id: 'purchaser_phone',
      label: 'Purchaser Phone',
      value: (order: SalesOrderListItem) => getPhone(order.purchaser?.phone, formatPhone),
    }),
    createIndexTableExportColumn({
      id: 'ordering_contact',
      label: 'Ordering Contact',
      value: (order: SalesOrderListItem) => order.ordering_contact?.name || '',
    }),
    createIndexTableExportColumn({
      id: 'ordering_contact_email',
      label: 'Ordering Contact Email',
      value: (order: SalesOrderListItem) => order.ordering_contact?.email || '',
    }),
    createIndexTableExportColumn({
      id: 'ordering_contact_phone',
      label: 'Ordering Contact Phone',
      value: (order: SalesOrderListItem) => getPhone(order.ordering_contact?.phone, formatPhone),
    }),
    createIndexTableExportColumn({
      id: 'recipient_name',
      label: 'Recipient Name',
      value: (order: SalesOrderListItem) => order.recipient_name || '',
    }),
    createIndexTableExportColumn({
      id: 'recipient_email',
      label: 'Recipient Email',
      value: (order: SalesOrderListItem) => order.recipient_email || '',
    }),
    createIndexTableExportColumn({
      id: 'recipient_phone',
      label: 'Recipient Phone',
      value: (order: SalesOrderListItem) => getPhone(order.recipient_phone, formatPhone),
    }),
    createIndexTableExportColumn({
      id: 'ordered_at',
      label: 'Ordered At',
      type: 'datetime',
      value: (order: SalesOrderListItem) => order.created_at,
    }),
    createIndexTableExportColumn({
      id: 'fulfillment_due',
      label: 'Delivery / pickup date',
      type: 'date',
      value: (order: SalesOrderListItem) => order.fulfillment_due_date,
    }),
    createIndexTableExportColumn({
      id: 'payment_due',
      label: 'Payment Due',
      type: 'date',
      value: (order: SalesOrderListItem) => order.due_date,
    }),
    createIndexTableExportColumn({
      id: 'fulfillment_type',
      label: 'Fulfillment Type',
      value: (order: SalesOrderListItem) => getEnumLabel(FulfillmentType, order.fulfillment_type),
    }),
    createIndexTableExportColumn({
      id: 'fulfillment_status',
      label: 'Fulfillment Status',
      value: (order: SalesOrderListItem) =>
        getEnumLabel(SalesOrderFulfillmentStatus, order.fulfillment_status),
    }),
    createIndexTableExportColumn({
      id: 'payment_status',
      label: 'Payment Status',
      value: (order: SalesOrderListItem) => getSalesOrderPaymentStatusLabel(order),
    }),
    createIndexTableExportColumn({
      id: 'refund_status',
      label: 'Refund Status',
      value: (order: SalesOrderListItem) =>
        getEnumLabel(SalesOrderRefundStatus, order.refund_status),
    }),
    ...createAddressExportColumns({
      idPrefix: 'delivery_address',
      labelPrefix: 'Delivery Address',
      getAddress: (order: SalesOrderListItem) => order.delivery_address,
      getName: (order: SalesOrderListItem) => order.delivery_destination_name,
      includeVerified: true,
    }),
    createIndexTableExportColumn({
      id: 'card_message',
      label: 'Card Message',
      value: (order: SalesOrderListItem) => order.card_message || '',
    }),
    createIndexTableExportColumn({
      id: 'signature',
      label: 'Signature',
      value: (order: SalesOrderListItem) => order.signature || '',
    }),
    createIndexTableExportColumn({
      id: 'special_instructions',
      label: 'Special Instructions',
      value: (order: SalesOrderListItem) => order.special_instructions || '',
    }),
    createIndexTableExportColumn({
      id: 'payment_terms',
      label: 'Payment Terms',
      value: (order: SalesOrderListItem) =>
        getEnumLabel(CustomerPaymentTerms, order.payment_terms_code),
    }),
    createIndexTableExportColumn({
      id: 'payment_terms_days',
      label: 'Payment Terms Days',
      type: 'integer',
      value: (order: SalesOrderListItem) => order.payment_terms_days ?? null,
    }),
    createIndexTableExportColumn({
      id: 'allow_fulfillment_before_payment',
      label: 'Allow Fulfillment Before Payment',
      type: 'boolean',
      value: (order: SalesOrderListItem) => Boolean(order.allow_fulfillment_before_payment),
    }),
    createIndexTableExportColumn({
      id: 'tax_exempt',
      label: 'Tax Exempt',
      type: 'boolean',
      value: (order: SalesOrderListItem) => Boolean(order.applied_tax_exempt),
    }),
    createIndexTableExportColumn({
      id: 'item_count',
      label: 'Item Count',
      type: 'integer',
      value: (order: SalesOrderListItem) => order.item_count ?? 0,
    }),
    createIndexTableExportColumn({
      id: 'subtotal',
      label: 'Subtotal',
      type: 'currency',
      value: (order: SalesOrderListItem) => getMoney(order.document?.net_subtotal),
    }),
    createIndexTableExportColumn({
      id: 'delivery_fee',
      label: 'Delivery',
      type: 'currency',
      value: (order: SalesOrderListItem) => getMoney(order.document?.delivery_fee),
    }),
    createIndexTableExportColumn({
      id: 'shipping_fee',
      label: 'Shipping',
      type: 'currency',
      value: (order: SalesOrderListItem) => getMoney(order.document?.shipping_fee),
    }),
    createIndexTableExportColumn({
      id: 'variable_service_fee',
      label: 'Service Fee',
      type: 'currency',
      value: (order: SalesOrderListItem) => getMoney(order.document?.variable_service_fee),
    }),
    createIndexTableExportColumn({
      id: 'florist_fee',
      label: 'Shop Fee',
      type: 'currency',
      value: (order: SalesOrderListItem) => getKnownMoney(order.financial_state.shop_fee_total),
    }),
    createIndexTableExportColumn({
      id: 'florist_fee_tax_amount',
      label: 'Shop Fee Tax',
      type: 'currency',
      value: (order: SalesOrderListItem) => getKnownMoney(order.financial_state.shop_fee_tax_total),
    }),
    createIndexTableExportColumn({
      id: 'tax_amount',
      label: 'Tax',
      type: 'currency',
      value: (order: SalesOrderListItem) => getMoney(order.document?.tax_amount),
    }),
    createIndexTableExportColumn({
      id: 'variable_service_fee_tax_amount',
      label: 'Service Fee Tax (OneStem)',
      type: 'currency',
      value: (order: SalesOrderListItem) =>
        getMoney(order.document?.variable_service_fee_tax_amount),
    }),
    createIndexTableExportColumn({
      id: 'florist_tax_amount',
      label: 'Sales Tax (Your Shop)',
      type: 'currency',
      value: (order: SalesOrderListItem) => getMoney(order.financial_state.florist_tax_total),
    }),
    createIndexTableExportColumn({
      id: 'relay_fee',
      label: 'Relay Fee',
      type: 'currency',
      value: (order: SalesOrderListItem) => getMoney(order.document?.relay_fee),
    }),
    createIndexTableExportColumn({
      id: 'tip_amount',
      label: 'Tip',
      type: 'currency',
      value: (order: SalesOrderListItem) => getMoney(order.document?.tip_amount),
    }),
    createIndexTableExportColumn({
      id: 'total',
      label: 'Invoice Total',
      type: 'currency',
      value: (order: SalesOrderListItem) => getMoney(order.financial_state.document_total),
    }),
    createIndexTableExportColumn({
      id: 'customer_total',
      label: 'Total Including Shop Fees',
      type: 'currency',
      value: (order: SalesOrderListItem) => getKnownMoney(order.financial_state.order_total),
    }),
    createIndexTableExportColumn({
      id: 'paid_total',
      label: 'Paid Total',
      type: 'currency',
      value: (order: SalesOrderListItem) => getSalesOrderGrossPaidTotal(order),
    }),
    createIndexTableExportColumn({
      id: 'refunded_total',
      label: 'Refunded Total',
      type: 'currency',
      value: (order: SalesOrderListItem) => getSalesOrderGrossRefundedTotal(order),
    }),
    createIndexTableExportColumn({
      id: 'net_collected_total',
      label: 'Net Collected',
      type: 'currency',
      value: (order: SalesOrderListItem) => getSalesOrderNetCollectedTotal(order),
    }),
    createIndexTableExportColumn({
      id: 'open_balance',
      label: 'Open Balance',
      type: 'currency',
      value: (order: SalesOrderListItem) => getSalesOrderRemainingTotal(order),
    }),
    createIndexTableExportColumn({
      id: 'collection_status',
      label: 'Collection Status',
      value: (order: SalesOrderListItem) => getSalesOrderCollectionStatus(order) || '',
    }),
    createIndexTableExportColumn({
      id: 'collection_closed_reason',
      label: 'Collection Closed Reason',
      value: (order: SalesOrderListItem) => getSalesOrderCollectionClosedReason(order) || '',
    }),
  ].filter((column) => column !== null)
}

function getLineItemUnitAmount(lineItem: SalesOrderDocumentLine): number {
  return normalizeCurrencyValue(lineItem.amount)
}

function getLineItemTotal(lineItem: SalesOrderDocumentLine): number {
  return normalizeCurrencyValue(lineItem.gross_total)
}

// The index never loads a line's live variant: its snapshot carries the item.
function getExportLineItemDisplay(lineItem: SalesOrderDocumentLine) {
  return getLineItemDisplay({ variant_details: lineItem.sales_order_item?.variant_details })
}

function getLineItemTitle(lineItem: SalesOrderDocumentLine): string {
  if (lineItem.type !== TransactionLineType.Product.Value) {
    return lineItem.memo || getEnumLabel(TransactionLineType, lineItem.type)
  }

  return getExportLineItemDisplay(lineItem).primaryName
}

function getLineItemVariant(lineItem: SalesOrderDocumentLine): string {
  if (lineItem.type !== TransactionLineType.Product.Value) {
    return ''
  }

  return getExportLineItemDisplay(lineItem).badgeLabel || ''
}

function getLineItemDescription(lineItem: SalesOrderDocumentLine): string {
  if (lineItem.type !== TransactionLineType.Product.Value) {
    return ''
  }

  return getExportLineItemDisplay(lineItem).description || ''
}

export function getSalesOrderLineItemExportRows(
  orders: SalesOrderListItem[] = []
): SalesOrderLineItemExportRow[] {
  return orders.flatMap((order) =>
    (order.document?.line_items ?? []).map((lineItem, index) => ({
      order,
      lineItem,
      lineNumber: index + 1,
    }))
  )
}

export function getSalesOrderLineItemExportColumns(
  showOrganization = false
): ExportColumn<SalesOrderLineItemExportRow>[] {
  return [
    createIndexTableExportColumn({
      id: 'order_id',
      label: 'Order ID',
      type: 'integer',
      value: ({ order }: SalesOrderLineItemExportRow) => order.id,
    }),
    createIndexTableExportColumn({
      id: 'reference_id',
      label: 'Reference ID',
      value: ({ order }: SalesOrderLineItemExportRow) => order.reference_id || '',
    }),
    showOrganization
      ? createIndexTableExportColumn({
          id: 'organization',
          label: 'Organization',
          value: ({ order }: SalesOrderLineItemExportRow) => order.organization_name || '',
        })
      : null,
    createIndexTableExportColumn({
      id: 'order_created',
      label: 'Order Created',
      type: 'datetime',
      value: ({ order }: SalesOrderLineItemExportRow) => order.created_at,
    }),
    createIndexTableExportColumn({
      id: 'fulfillment_due',
      label: 'Delivery / pickup date',
      type: 'date',
      value: ({ order }: SalesOrderLineItemExportRow) => order.fulfillment_due_date,
    }),
    createIndexTableExportColumn({
      id: 'fulfillment_type',
      label: 'Fulfillment Type',
      value: ({ order }: SalesOrderLineItemExportRow) =>
        getEnumLabel(FulfillmentType, order.fulfillment_type),
    }),
    createIndexTableExportColumn({
      id: 'fulfillment_status',
      label: 'Fulfillment Status',
      value: ({ order }: SalesOrderLineItemExportRow) =>
        getEnumLabel(SalesOrderFulfillmentStatus, order.fulfillment_status),
    }),
    createIndexTableExportColumn({
      id: 'payment_status',
      label: 'Payment Status',
      value: ({ order }: SalesOrderLineItemExportRow) => getSalesOrderPaymentStatusLabel(order),
    }),
    createIndexTableExportColumn({
      id: 'purchaser',
      label: 'Purchaser',
      value: ({ order }: SalesOrderLineItemExportRow) => order.purchaser?.name || '',
    }),
    createIndexTableExportColumn({
      id: 'recipient',
      label: 'Recipient',
      value: ({ order }: SalesOrderLineItemExportRow) => order.recipient_name || '',
    }),
    createIndexTableExportColumn({
      id: 'line_number',
      label: 'Line Number',
      type: 'integer',
      value: ({ lineNumber }: SalesOrderLineItemExportRow) => lineNumber,
    }),
    createIndexTableExportColumn({
      id: 'line_item_id',
      label: 'Line Item ID',
      type: 'integer',
      value: ({ lineItem }: SalesOrderLineItemExportRow) => lineItem.id,
    }),
    createIndexTableExportColumn({
      id: 'line_type',
      label: 'Line Type',
      value: ({ lineItem }: SalesOrderLineItemExportRow) =>
        getEnumLabel(TransactionLineType, lineItem.type),
    }),
    createIndexTableExportColumn({
      id: 'item',
      label: 'Item',
      value: ({ lineItem }: SalesOrderLineItemExportRow) => getLineItemTitle(lineItem),
    }),
    createIndexTableExportColumn({
      id: 'variant',
      label: 'Variant',
      value: ({ lineItem }: SalesOrderLineItemExportRow) => getLineItemVariant(lineItem),
    }),
    createIndexTableExportColumn({
      id: 'description',
      label: 'Description',
      value: ({ lineItem }: SalesOrderLineItemExportRow) => getLineItemDescription(lineItem),
    }),
    createIndexTableExportColumn({
      id: 'memo',
      label: 'Memo',
      value: ({ lineItem }: SalesOrderLineItemExportRow) => lineItem.memo || '',
    }),
    createIndexTableExportColumn({
      id: 'item_kind',
      label: 'Item Kind',
      value: ({ lineItem }: SalesOrderLineItemExportRow) =>
        getExportLineItemDisplay(lineItem).snapshot?.kind || '',
    }),
    createIndexTableExportColumn({
      id: 'product_id',
      label: 'Product ID',
      type: 'integer',
      value: ({ lineItem }: SalesOrderLineItemExportRow) =>
        getExportLineItemDisplay(lineItem).productId ?? null,
    }),
    createIndexTableExportColumn({
      id: 'recipe',
      label: 'Recipe',
      value: ({ lineItem }: SalesOrderLineItemExportRow) =>
        getExportLineItemDisplay(lineItem).recipe || '',
    }),
    createIndexTableExportColumn({
      id: 'quantity',
      label: 'Quantity',
      type: 'number',
      value: ({ lineItem }: SalesOrderLineItemExportRow) => lineItem.quantity,
    }),
    createIndexTableExportColumn({
      id: 'unit_amount',
      label: 'Unit Amount',
      type: 'currency',
      value: ({ lineItem }: SalesOrderLineItemExportRow) => getLineItemUnitAmount(lineItem),
    }),
    createIndexTableExportColumn({
      id: 'line_total',
      label: 'Line Total',
      type: 'currency',
      value: ({ lineItem }: SalesOrderLineItemExportRow) => getLineItemTotal(lineItem),
    }),
  ].filter((column) => column !== null)
}

export function getSalesOrderTransactionExportRows(
  orders: SalesOrderListItem[] = []
): SalesOrderTransactionExportRow[] {
  return orders.flatMap((order) =>
    // The Transactions sheet lists the order's money movements; what it sells is the Line Items sheet.
    (order.cash_activity ?? []).map((transaction, index) => ({
      order,
      transaction,
      transactionNumber: index + 1,
    }))
  )
}

export function getSalesOrderTransactionExportColumns(
  showOrganization = false
): ExportColumn<SalesOrderTransactionExportRow>[] {
  return [
    createIndexTableExportColumn({
      id: 'order_id',
      label: 'Order ID',
      type: 'integer',
      value: ({ order }: SalesOrderTransactionExportRow) => order.id,
    }),
    createIndexTableExportColumn({
      id: 'reference_id',
      label: 'Reference ID',
      value: ({ order }: SalesOrderTransactionExportRow) => order.reference_id || '',
    }),
    showOrganization
      ? createIndexTableExportColumn({
          id: 'organization',
          label: 'Organization',
          value: ({ order }: SalesOrderTransactionExportRow) => order.organization_name || '',
        })
      : null,
    createIndexTableExportColumn({
      id: 'transaction_number',
      label: 'Transaction Number',
      type: 'integer',
      value: ({ transactionNumber }: SalesOrderTransactionExportRow) => transactionNumber,
    }),
    createIndexTableExportColumn({
      id: 'transaction_type',
      label: 'Transaction Type',
      value: ({ transaction }: SalesOrderTransactionExportRow) =>
        getEnumLabel(TransactionType, transaction.type),
    }),
    createIndexTableExportColumn({
      id: 'transaction_source',
      label: 'Transaction Source',
      value: ({ transaction }: SalesOrderTransactionExportRow) =>
        getEnumLabel(TransactionSource, transaction.source),
    }),
    createIndexTableExportColumn({
      id: 'transaction_created',
      label: 'Transaction Created',
      type: 'datetime',
      value: ({ transaction }: SalesOrderTransactionExportRow) => transaction.created_at,
    }),
    createIndexTableExportColumn({
      id: 'subtotal',
      label: 'Subtotal',
      type: 'currency',
      value: ({ transaction }: SalesOrderTransactionExportRow) => getMoney(transaction.subtotal),
    }),
    createIndexTableExportColumn({
      id: 'discount',
      label: 'Discount',
      type: 'currency',
      value: ({ transaction }: SalesOrderTransactionExportRow) => getMoney(transaction.discount),
    }),
    createIndexTableExportColumn({
      id: 'freight',
      label: 'Freight',
      type: 'currency',
      value: ({ transaction }: SalesOrderTransactionExportRow) => getMoney(transaction.freight_fee),
    }),
    createIndexTableExportColumn({
      id: 'variable_service_fee',
      label: 'Service Fee',
      type: 'currency',
      value: ({ transaction }: SalesOrderTransactionExportRow) =>
        getMoney(transaction.variable_service_fee),
    }),
    createIndexTableExportColumn({
      id: 'florist_fee',
      label: 'Shop Fee',
      type: 'currency',
      value: ({ transaction }: SalesOrderTransactionExportRow) => getMoney(transaction.florist_fee),
    }),
    createIndexTableExportColumn({
      id: 'florist_fee_tax_amount',
      label: 'Shop Fee Tax',
      type: 'currency',
      value: ({ transaction }: SalesOrderTransactionExportRow) =>
        getMoney(transaction.florist_fee_tax_amount),
    }),
    createIndexTableExportColumn({
      id: 'tax',
      label: 'Tax',
      type: 'currency',
      value: ({ transaction }: SalesOrderTransactionExportRow) => getMoney(transaction.tax_amount),
    }),
    createIndexTableExportColumn({
      id: 'variable_service_fee_tax_amount',
      label: 'Service Fee Tax (OneStem)',
      type: 'currency',
      value: ({ transaction }: SalesOrderTransactionExportRow) =>
        getMoney(transaction.variable_service_fee_tax_amount),
    }),
    createIndexTableExportColumn({
      id: 'florist_tax_amount',
      label: 'Sales Tax (Your Shop)',
      type: 'currency',
      value: ({ transaction }: SalesOrderTransactionExportRow) =>
        getMoney(transaction.florist_tax_amount),
    }),
    createIndexTableExportColumn({
      id: 'relay_fee',
      label: 'Relay Fee',
      type: 'currency',
      value: ({ transaction }: SalesOrderTransactionExportRow) => getMoney(transaction.relay_fee),
    }),
    createIndexTableExportColumn({
      id: 'tip',
      label: 'Tip',
      type: 'currency',
      value: ({ transaction }: SalesOrderTransactionExportRow) => getMoney(transaction.tip_amount),
    }),
    createIndexTableExportColumn({
      id: 'total',
      label: 'Total',
      type: 'currency',
      value: ({ transaction }: SalesOrderTransactionExportRow) => getMoney(transaction.total),
    }),
    createIndexTableExportColumn({
      id: 'currency',
      label: 'Currency',
      value: ({ transaction }: SalesOrderTransactionExportRow) => transaction.currency_type || '',
    }),
    createIndexTableExportColumn({
      id: 'payment_id',
      label: 'Payment ID',
      type: 'integer',
      value: ({ transaction }: SalesOrderTransactionExportRow) => transaction.payment.id,
    }),
    createIndexTableExportColumn({
      id: 'source_payment_id',
      label: 'Source Payment ID',
      type: 'integer',
      value: ({ transaction }: SalesOrderTransactionExportRow) =>
        transaction.payment.source_payment_id,
    }),
    createIndexTableExportColumn({
      id: 'stripe_payment_intent_id',
      label: 'Stripe Payment Intent ID',
      value: ({ transaction }: SalesOrderTransactionExportRow) =>
        transaction.payment.stripe_payment_intent_id || '',
    }),
    createIndexTableExportColumn({
      id: 'payment_reference',
      label: 'Payment Reference',
      value: ({ transaction }: SalesOrderTransactionExportRow) =>
        transaction.payment.reference || '',
    }),
    createIndexTableExportColumn({
      id: 'payment_type',
      label: 'Payment Type',
      value: ({ transaction }: SalesOrderTransactionExportRow) => {
        // payment-method-display is untyped JavaScript; it returns a label or null.
        const methodLabel: unknown =
          transaction.payment.type === PaymentType.Card.Value &&
          formatStripePaymentMethodType(transaction.payment.stripe_payment_method)

        return (
          (typeof methodLabel === 'string' && methodLabel) ||
          getEnumLabel(PaymentType, transaction.payment.type)
        )
      },
    }),
    createIndexTableExportColumn({
      id: 'payment_status',
      label: 'Payment Status',
      value: ({ transaction }: SalesOrderTransactionExportRow) =>
        getEnumLabel(PaymentStatus, transaction.payment.status),
    }),
    createIndexTableExportColumn({
      id: 'payment_refund_status',
      label: 'Payment Refund Status',
      value: ({ transaction }: SalesOrderTransactionExportRow) =>
        getEnumLabel(PaymentRefundStatus, transaction.payment.refund_status),
    }),
    createIndexTableExportColumn({
      id: 'payment_amount',
      label: 'Payment Amount',
      type: 'currency',
      value: ({ transaction }: SalesOrderTransactionExportRow) =>
        getMoney(transaction.payment.amount),
    }),
    createIndexTableExportColumn({
      id: 'payment_amount_requested',
      label: 'Payment Amount Requested',
      type: 'currency',
      value: ({ transaction }: SalesOrderTransactionExportRow) =>
        getMoney(transaction.payment.amount_requested),
    }),
    createIndexTableExportColumn({
      id: 'payment_amount_settled',
      label: 'Payment Amount Settled',
      type: 'currency',
      value: ({ transaction }: SalesOrderTransactionExportRow) =>
        getMoney(transaction.payment.amount_settled),
    }),
  ].filter((column) => column !== null)
}

export function buildSalesOrderExportSheets({
  orders = [],
  showOrganization,
  formatPhone,
}: {
  orders?: SalesOrderListItem[]
  showOrganization?: boolean
  formatPhone?: PhoneFormatter['formatPhone']
}): [
  IndexTableExportSheet<SalesOrderListItem>,
  IndexTableExportSheet<SalesOrderLineItemExportRow>,
  IndexTableExportSheet<SalesOrderTransactionExportRow>,
] {
  return [
    {
      sheetName: 'Orders',
      columns: getSalesOrderSummaryExportColumns(showOrganization, formatPhone),
      rows: orders,
    },
    {
      sheetName: 'Line Items',
      columns: getSalesOrderLineItemExportColumns(showOrganization),
      rows: getSalesOrderLineItemExportRows(orders),
    },
    {
      sheetName: 'Transactions',
      columns: getSalesOrderTransactionExportColumns(showOrganization),
      rows: getSalesOrderTransactionExportRows(orders),
    },
  ]
}
